import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from '@apollo/react-hooks';

import { Pagination } from 'components';
import ConfirmContext from 'contexts/ConfirmContext';

import { RENTAL_ITEMS } from 'graphql/queries/rental-items';
import { DELETE_RENTAL_ITEMS } from 'graphql/mutations/rental-items';

import { TrashIcon, EditIcon } from 'components/icons';

import {
  Typography,
  IconButton,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  TableBody,
  Table
} from '@material-ui/core';

import useStyles from './styles';

const headingsConfig = classes => {
  return [
    { label: 'Photo', cellClassName: classes.imgCell, align: 'left' },
    {
      label: 'Rental Items name',
      cellClassName: classes.nameCell,
      align: 'left'
    },
    {
      label: 'Available Units',
      cellClassName: classes.availabilityCell,
      align: 'left'
    },
    {
      label: 'Status',
      cellClassName: classes.scheduleCell,
      align: 'left'
    },
    {
      label: 'Product code',
      cellClassName: classes.productCodeCell,
      align: 'left'
    },
    { label: 'Item Type', cellClassName: classes.guestsCell, align: 'left' },
    { label: 'Actions', align: 'right' }
  ];
};

function RentalItemsList({ deleteCallback, ownerId }) {
  const classes = useStyles();
  const [t] = useTranslation();
  const history = useHistory();
  const confirm = React.useContext(ConfirmContext);
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = React.useState(0);

  const { data, refetch } = useQuery(RENTAL_ITEMS, {
    variables: {
      businessOwnerId: ownerId,
      page: page
    }
  });

  const [deleteRentalItems] = useMutation(DELETE_RENTAL_ITEMS);

  React.useEffect(() => {
    refetch();
  }, []);

  const onDeleteHandler = item => {
    confirm({
      title: 'Delete Rental Items?',
      description: `${item.title} • ${item.productCode}`,
      confirmationText: 'Delete',
      cancellationText: 'Back'
    })
      .then(() =>
        deleteRentalItems({ variables: { id: item.id } }).then(() => {
          deleteCallback();
          enqueueSnackbar(t('Rental Items was successfully removed!'), {
            variant: 'success'
          });
          if (data.rentalItems.length <= 1 && page) {
            refetch({
              page: page - 1
            });
            setPage(page - 1);
          } else {
            refetch({ page });
          }
        })
      )
      .catch(() => {});
  };

  const onPageChangeHandler = (e, page) => {
    refetch({
      page: page - 1
    });
    setPage(page - 1);
  };

  return (
    <TableContainer component="div" classes={{ root: classes.container }}>
      <Table className={classes.table} aria-label="simple table">
        <TableHead className={classes.tableHead}>
          <TableRow>
            {headingsConfig(classes).map(heading => (
              <TableCell
                key={heading.label}
                align={heading.align}
                className={classNames(
                  classes.tableCell,
                  classes.headTableCell,
                  heading.cellClassName
                )}
              >
                <Typography variant="h3" className={classes.heading}>
                  {t(heading.label)}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data &&
            data.rentalItems &&
            data.rentalItems.map(rentalItems => (
              <TableRow
                key={rentalItems.id}
                classes={{ root: classes.tableRow }}
                onClick={() => history.push(`/rental-items/${rentalItems.id}`)}
              >
                <TableCell
                  className={classNames(classes.tableCell, classes.imgCell)}
                  component="th"
                  scope="row"
                >
                  <div className={classes.imageWrapper}>
                    {rentalItems.photo && (
                      <img
                        src={rentalItems.photo}
                        alt=""
                        className={classes.image}
                      />
                    )}
                  </div>
                </TableCell>

                <TableCell
                  className={classNames(classes.tableCell, classes.nameCell)}
                  align="left"
                >
                  {rentalItems.title}
                </TableCell>
                <TableCell
                  className={classNames(
                    classes.tableCell,
                    classes.availabilityCell
                  )}
                  align="left"
                >
                  {rentalItems.availableUnits}
                </TableCell>
                <TableCell
                  className={classNames(
                    classes.tableCell,
                    classes.scheduleCell
                  )}
                  align="left"
                >
                  {rentalItems.status.status}
                </TableCell>
                <TableCell
                  className={classNames(
                    classes.tableCell,
                    classes.productCodeCell
                  )}
                  align="left"
                >
                  {rentalItems.productCode}
                </TableCell>
                <TableCell
                  className={classNames(classes.tableCell, classes.guestsCell)}
                  align="left"
                >
                  {rentalItems.itemsType.type}
                </TableCell>
                <TableCell className={classes.tableCell} align="right">
                  <div style={{ display: 'inline-flex', paddingRight: '10px' }}>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        history.push(`/rental-items/${rentalItems.id}`);
                      }}
                      className={classes.iconButton}
                      disableTouchRipple={true}
                      disableFocusRipple={true}
                      aria-label="edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        onDeleteHandler(rentalItems);
                      }}
                      disableTouchRipple={true}
                      disableFocusRipple={true}
                      className={classes.iconButton}
                      aria-label="delete"
                    >
                      <TrashIcon />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      {data && (
        <Pagination
          page={page + 1}
          count={data.numberOfRentalItemsPages}
          onChange={onPageChangeHandler}
          amount={`${page * 11 + 1}-${Math.min(
            (page + 1) * 11,
            data.rentalItemsOverview.rentalItems.total
          )}`}
          total={data.rentalItemsOverview.rentalItems.total}
        />
      )}
    </TableContainer>
  );
}

export default RentalItemsList;
